import { useRef, useState } from 'react'
import type { ArtikelPosition } from '@/api/warehouse'
import { ApiError, apiFetch, isTransportError } from '@/api/client'
import { parseKorrekturDelta } from '@/lib/handregal-korrektur'
import { newIdempotencyKey } from '@/lib/offline-queue'
import { warehouseErrorMessage } from '@/lib/warehouse-company'

export function LeiterPaletteInline({ position: p, unit, onSaved }: {
  position: ArtikelPosition; unit: 'm' | 'St.'; onSaved: () => void
}) {
  const [offen, setOffen] = useState(false)
  const [text, setText] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [unklar, setUnklar] = useState(false)
  const [paare, setPaare] = useState(false)
  const [fehler, setFehler] = useState('')
  const bodyRef = useRef<string | null>(null)
  const keyRef = useRef(newIdempotencyKey())
  const vorher = p.state === 'verschlossen' ? p.qtyExact : p.qtyCirca
  const proKarton = p.piecesPerCarton
  const delta = parseKorrekturDelta(text)
  const nachher = vorher === null || delta === null ? null : vorher + delta
  const valid = nachher !== null && nachher >= 0 && nachher <= 2_147_483_647
    && (proKarton === null || proKarton > 0) && reason.trim().length >= 5
  async function buchen(bestaetigt = false) {
    if (busy || (!valid && !unklar) || nachher === null) return
    const body = bodyRef.current ?? JSON.stringify({ cartons: proKarton === null ? 0 : Math.floor(nachher / proKarton),
      pieces_per_carton: proKarton, rest_pieces: proKarton === null ? nachher : nachher % proKarton,
      reason: reason.trim(), idempotency_key: keyRef.current, device_label: 'Grundartikel Web',
      vorher: { qty: vorher, cartons: p.cartons, pieces_per_carton: p.piecesPerCarton, state: p.state },
      ...(bestaetigt ? { paare_bestaetigt: true } : {}) })
    setBusy(true); setFehler('')
    try {
      await apiFetch(`/warehouse/position/${p.id}/leiter-korrektur/`, { method: 'POST', body })
      setOffen(false); setText(''); setReason(''); setUnklar(false); setPaare(false)
      bodyRef.current = null; keyRef.current = newIdempotencyKey()
      onSaved()
    } catch (error) {
      setFehler(warehouseErrorMessage(error))
      const frage = error instanceof ApiError && error.code === 'paare_pruefen'
        && error.status === 409 && !error.undecodable && error.details['not_booked_key'] === keyRef.current
      const unsicher = (unklar || isTransportError(error) || !(error instanceof ApiError && error.status >= 400 && error.status < 500)) && !frage
      setUnklar(unsicher); bodyRef.current = unsicher ? body : null; setPaare(frage)
    } finally { setBusy(false) }
  }
  if (!offen) return <button type="button" onClick={() => setOffen(true)}>± Menge</button>
  return <form aria-label={`Palette ${p.paletteNo ?? p.id} korrigieren`} onSubmit={e => { e.preventDefault(); void buchen() }}>
    <input aria-label="Korrektur Palette" placeholder="+5 oder −3" value={text} disabled={busy || unklar}
      onChange={e => { setText(e.target.value); setPaare(false) }} />
    <input aria-label="Grund Palette" placeholder="Grund (mindestens 5 Zeichen)" maxLength={200}
      value={reason} disabled={busy || unklar} onChange={e => setReason(e.target.value)} />
    {nachher !== null && <div>{vorher} {delta! < 0 ? '−' : '+'} {Math.abs(delta!)} = {nachher} {unit}</div>}
    {vorher === null && <div role="status">Menge unbekannt. Bitte Palette zuerst prüfen.</div>}
    {nachher !== null && nachher < 0 && <div role="alert">Ergebnis wäre unter 0</div>}
    {fehler && <div role="alert">{fehler}</div>}
    <button type="submit" disabled={busy || (!valid && !unklar)}>{unklar ? 'Erneut prüfen' : 'Buchen'}</button>
    {paare && <button type="button" disabled={busy || !valid} onClick={() => void buchen(true)}>Ja, Stück korrekt</button>}
    <button type="button" disabled={busy || unklar} onClick={() => setOffen(false)}>Abbrechen</button>
  </form>
}
